import type { LayerConfiguration, LayerDefinition, LayerItemType } from "../../../../models/mapConfiguration";

const SEGMENT_SERVER_KEYS = [
	"brandIds",
	"marketerIds",
	"channelIds",
	"stateIds",
] as const satisfies readonly (keyof LayerConfiguration)[];

const DATE_SERVER_KEYS = [
	"dateRange",
	"startDateTime",
	"endDateTime",
] as const satisfies readonly (keyof LayerConfiguration)[];

const SALES_SERVER_KEYS = [
	...SEGMENT_SERVER_KEYS,
	"reps",
	"machines",
	"enrollmentId",
	...DATE_SERVER_KEYS,
	"goodSales",
	"phoneCarrierType",
] as const satisfies readonly (keyof LayerConfiguration)[];

const REPS_MACHINES_SERVER_KEYS = [
	...SEGMENT_SERVER_KEYS,
	...DATE_SERVER_KEYS,
	"enrollmentId",
	"minAccuracy",
] as const satisfies readonly (keyof LayerConfiguration)[];

const RETAIL_SERVER_KEYS = [
	"marketerIds",
	"assignedUserIds",
	"assignedOfficeIds",
] as const satisfies readonly (keyof LayerConfiguration)[];

const SERVER_FILTER_KEYS: Partial<Record<LayerItemType, readonly (keyof LayerConfiguration)[]>> = {
	Representatives: [...REPS_MACHINES_SERVER_KEYS, "activeRepOnly", "reps"],
	Machines: [...REPS_MACHINES_SERVER_KEYS, "activeMachineOnly", "machines"],
	Sales: SALES_SERVER_KEYS,
	RetailLocations: RETAIL_SERVER_KEYS,
	Doors: RETAIL_SERVER_KEYS,
};

function serverKeys(layer: LayerDefinition | undefined) {
	return (layer?.itemType && SERVER_FILTER_KEYS[layer.itemType]) || [];
}

export function snapshotServerFilters(layer: LayerDefinition | undefined): Partial<LayerConfiguration> {
	const snapshot: Partial<LayerConfiguration> = {};
	if (!layer) return snapshot;

	for (const key of serverKeys(layer)) {
		(snapshot as any)[key] = layer[key];
	}
	return snapshot;
}

export function hasExecuted(layer: LayerDefinition) {
	return (layer.executeCount ?? 0) > 0 && !!layer.executedFilters;
}

export function markExecuted(layer: LayerDefinition): LayerDefinition {
	return { ...layer, executeCount: (layer.executeCount ?? 0) + 1, executedFilters: snapshotServerFilters(layer) };
}

export function hasPendingServerChanges(layer: LayerDefinition): boolean {
	if (!layer.itemType) return false;
	if (!hasExecuted(layer)) return true;

	const executed = layer.executedFilters;
	return serverKeys(layer).some(
		(key) => JSON.stringify(layer[key] ?? null) !== JSON.stringify(executed[key] ?? null),
	);
}
